import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService, NotificationFilters } from '../services';

export const useNotifications = (filters?: NotificationFilters) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications', filters],
    queryFn: async () => {
      const response = await notificationService.getNotifications(filters);
      return response.data;
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => notificationService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const deleteAllReadMutation = useMutation({
    mutationFn: () => notificationService.deleteAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  return {
    notifications: data,
    isLoading,
    error,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    deleteNotification: deleteMutation.mutateAsync,
    deleteAllRead: deleteAllReadMutation.mutateAsync,
  };
};

export const useUnreadNotifications = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: async () => {
      const response = await notificationService.getUnreadNotifications();
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  return {
    notifications: data,
    isLoading,
    error,
  };
};

export const useUnreadCount = () => {
  // Get user from localStorage to check user type
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isSuperAdmin = user?.user_type === 'superadmin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await notificationService.getUnreadCount();
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: !isSuperAdmin, // Only enable for county admins, not superadmins
  });

  return {
    count: data?.count ?? 0,
    isLoading,
    error,
  };
};
